import React, {
  FC,
  useEffect,
  useRef,
  useState,
} from "react"
import localforage from "localforage"

// Constants
import { BREAKPOINTS } from "@constants"
import { debounce, throttle, isClientSide, isTouchDevice } from "@utils"

// Store
import {
  addNewQuestion,
  addQuestionToInterview,
  exportToPdf,
  removeQuestionFromInterview,
  resetState,
  stopDraggingQuestion,
  updateDropZoneIndex,
} from "../../_store/actions"
import {
  GA_CUSTOM_EVENTS,
  LOCAL_FORAGE_KEY,
  QUESTION_LIST_VARIANTS,
  STATES,
  STATE_PRESETS,
} from "../../_store/constants"
import { DispatchType, StateType } from "../../_store/types"

// Utils
import { logCustomGAEvent } from "../../_utils"

// IB Components
import DragContainer from "./_components/DragContainer/DragContainer"
import ExportModal from "./_components/ExportModal/ExportModal"
import QuestionList from "./_components/QuestionList/QuestionList"

// Styled components
import {
  InterviewBuilderAppGridDIV
} from "./InterviewBuilderApp.styles"


type InterviewBuilderAppProps = {
  statePreset: STATE_PRESETS
  state: StateType
  dispatch: DispatchType
}

type XYCoordinates = [x: number, y: number]

const InterviewBuilderApp: FC<InterviewBuilderAppProps> = ({
  statePreset,
  state,
  dispatch,
}) => {
  const elRef = useRef<HTMLDivElement | null>(null)
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [currentCollapsedList, setCurrentCollapsedList] = useState<QUESTION_LIST_VARIANTS>(
    state.interview.questions.length > 0 ?
      QUESTION_LIST_VARIANTS.LIBRARY :
      QUESTION_LIST_VARIANTS.INTERVIEW
  )
  const [initialDragXY, setInitialDragXY] = useState<XYCoordinates | null>(null)
  const [dragXY, setDragXY] = useState<XYCoordinates>([0, 0])
  const [showExportModal, setShowExportModal] = useState<boolean>(false)

  // Hijack the window scroll event when the browser is fully scrolled into the app
  // and release when the mouse wheel / trackpad is scrolled with a tiny bit of force
  useEffect(() => {
    // This causes way too many issues on touch devices, so bail out!
    if (isTouchDevice()) return

    const handleWindowScroll = throttle((e: Event) => {
      const tabsEl = document.querySelector(".Tabs")
      if (!tabsEl) return

      const tabsRect = tabsEl.getBoundingClientRect()
      const isInAppView = tabsRect.top <= 0
      if (isInAppView) {
        window.scrollTo(0, window.scrollY + tabsRect.top)
        document.body.style.overflow = "hidden"
      }
    }, 100)

    const handleWindowWheel = throttle((e: WheelEvent) => {
      if (document.body.style.overflow === "hidden" && e.deltaY <= -30) {
        document.body.style.overflow = "auto"
      }
    }, 100)

    const handleWindowResize = throttle((e: Event) => {
      if (document.body.style.overflow === "hidden") {
        document.body.style.overflow = "auto"
      }
    }, 100)

    window.addEventListener("scroll", handleWindowScroll)
    window.addEventListener("wheel", handleWindowWheel)
    window.addEventListener("resize", handleWindowResize)

    return () => {
      window.removeEventListener("scroll", handleWindowScroll)
      window.removeEventListener("wheel", handleWindowWheel)
      window.removeEventListener("resize", handleWindowResize)
      document.body.style.overflow = "auto"
    }
  }, [])

  // Save the user's state to localforage (IndexedDB) whenever the state changes
  useEffect(() => {
    const stringifiedState = JSON.stringify({
      ...state,
      // Don't save current UI state
      isDraggingData: {
        cardClientRect: undefined,
        dropZoneIndex: undefined,
        questionId: undefined,
        questionIndex: undefined,
        questionOrigin: undefined,
      },
      isEditingQuestionId: undefined,
    })
    if (stringifiedState !== JSON.stringify(STATES[statePreset])) {
      // Clear any preset names in the query string
      history.replaceState({}, "", "/interview-builder/")

      // Save the current state to localforage
      localforage.setItem(LOCAL_FORAGE_KEY, stringifiedState)
    }
  }, [state])

  // If the user chooses to reset the state, reset the localforage entry as well
  const handleResetState = () => {
    dispatch(resetState(statePreset))
    localforage.setItem(LOCAL_FORAGE_KEY, JSON.stringify(STATES[statePreset]))
  }

  // Toggle `isMobile` when the browser width changes
  useEffect(() => {
    const handleWindowResize = debounce(() => {
      setIsMobile(window.innerWidth < BREAKPOINTS.M)
    }, 250)

    window.addEventListener("resize", handleWindowResize)

    handleWindowResize()

    return () => {
      window.removeEventListener("resize", handleWindowResize)
    }
  }, [])

  // All of the drag/drop handling is found in this useEffect!
  useEffect(() => {
    if (!elRef.current) return

    // Handle determining the index at which to render the Drop Zone (needs to be throttled because it is an expensive operation!)
    const determineDropZoneIndex = (
      clientXY: XYCoordinates,
      currentDraggingIndex: number,
      currentDraggingOrigin: QUESTION_LIST_VARIANTS,
    ): void => {
      const isFromLibrary = currentDraggingOrigin === QUESTION_LIST_VARIANTS.LIBRARY
      const elsAtPoint = [...document.elementsFromPoint(clientXY[0], clientXY[1])]
      const cardEl = elsAtPoint.find(
        el =>
          typeof el.className === "string" &&
          [...el.classList].includes("QuestionCard")
      ) as HTMLDivElement | undefined

      // If there's no card at this position, bail out!
      if (!cardEl) {
        // If the card is being dragged over the library, reset the drop zone
        if (
          elsAtPoint.find(el =>
            typeof el.className === "string" &&
            el.className.includes("is-LIBRARY")
          )
        ) {
          dispatch(updateDropZoneIndex())
          return
        }

        // If we're dragging over the InvisibleDropZone then we can place the
        // real drop zone index at the end of the list
        const invisibleDropZoneEl = elsAtPoint.find(
          el =>
            typeof el.className === "string" &&
            [...el.classList].includes("InvisibleDropZone")
        ) as HTMLDivElement | undefined

        if (invisibleDropZoneEl) {
          const lastIndex = Number(invisibleDropZoneEl.dataset.lastIndex)
          dispatch(updateDropZoneIndex(lastIndex + 1))
          return
        }

        return
      }

      // The card's index is stashed in a data attr (data-index) in QuestionCard
      const cardIndex = Number(cardEl.dataset.index)

      // If the card at this point is the card that's being dragged, bail out!
      if (!isFromLibrary && cardIndex === currentDraggingIndex) {
        dispatch(updateDropZoneIndex())
        return
      }

      const cardClientRect = cardEl.getBoundingClientRect()

      // If the cursor is in the top half of the card, update the drop zone index to be this card's index
      if (
        (cardClientRect.y <= clientXY[1]) &&
        (clientXY[1] <= cardClientRect.y + cardClientRect.height / 2)
      ) {
        // If the previous index is the same as the index of the card being dragged, bail out!
        if (!isFromLibrary && cardIndex - 1 === currentDraggingIndex) {
          dispatch(updateDropZoneIndex())
          return
        }
        dispatch(updateDropZoneIndex(cardIndex))
        return
      }

      // If the cursor is in the bottom half of the card, update the drop zone index to be this card's index + 1
      if (
        (clientXY[1] > cardClientRect.y + cardClientRect.height / 2) &&
        (clientXY[1] <= cardClientRect.y + cardClientRect.height)
      ) {
        // If the next card in the list is the one that's currently being dragged, bail out!
        if (!isFromLibrary && cardIndex + 1 === currentDraggingIndex) {
          dispatch(updateDropZoneIndex())
          return
        }
        dispatch(updateDropZoneIndex(cardIndex + 1))
        return
      }
    }
    const determineDropZoneIndexThrottled = throttle(determineDropZoneIndex, 50, { trailing: false })

    // Handle dragging the card (had to manually type the event because of some weirdness)
    const handleDragging = (e: MouseEvent | TouchEvent): void => {
      // Set defaults
      let pageXY: XYCoordinates = [0, 0]
      let clientXY: XYCoordinates = [0, 0]

      // Make sure everything expect is defined
      if (
        !state.isDraggingData.questionId ||
        typeof state.isDraggingData.questionIndex !== "number" ||
        !state.isDraggingData.questionOrigin ||
        !state.isDraggingData.cardClientRect ||
        !determineDropZoneIndexThrottled
      ) return

      e.preventDefault()

      // If this is a touch event, get the XYs from the touches array
      if (window.TouchEvent && e instanceof window.TouchEvent) {
        pageXY = [e.touches[0].pageX, e.touches[0].pageY]
        clientXY = [e.touches[0].clientX, e.touches[0].clientY]
      }

      // If this is a mouse event get the XYs from normal spot
      if (window.MouseEvent && e instanceof window.MouseEvent) {
        pageXY = [e.pageX, e.pageY]
        clientXY = [e.clientX, e.clientY]
      }

      const currentXY: XYCoordinates = [...pageXY]

      if (!initialDragXY) {
        setInitialDragXY(currentXY)
        return
      }

      setDragXY([
        -(initialDragXY[0] - currentXY[0]),
        -(initialDragXY[1] - currentXY[1])
      ])

      determineDropZoneIndexThrottled(
        clientXY,
        state.isDraggingData.questionIndex,
        state.isDraggingData.questionOrigin
      )
    }

    // Handle dropping (ending the drag) of the card
    const handleDragEnd = (e: MouseEvent | TouchEvent): void => {
      if (
        !state.isDraggingData.questionId ||
        typeof state.isDraggingData.questionIndex !== "number" ||
        !state.isDraggingData.questionOrigin ||
        !state.isDraggingData.cardClientRect
      ) return

      // Set defaults
      let clientXY: XYCoordinates = [0, 0]

      // If this is a touch event, get the XYs from the touches array
      if (window.TouchEvent && e instanceof window.TouchEvent) {
        clientXY = [e.changedTouches[0].clientX, e.changedTouches[0].clientY]
      }

      // If this is a mouse event get the XYs from normal spot
      if (window.MouseEvent && e instanceof window.MouseEvent) {
        clientXY = [e.clientX, e.clientY]
      }

      setInitialDragXY(null)
      setDragXY([0, 0])

      const elsAtPoint = [...document.elementsFromPoint(clientXY[0], clientXY[1])]
      const isOverRemoveDropZone = elsAtPoint.filter(el => el.id === "remove-drop-zone").length > 0

      if (isOverRemoveDropZone) {
        dispatch(removeQuestionFromInterview(state.isDraggingData.questionId))
      }

      const isOverAddDropZone = elsAtPoint.filter(el => el.id === "add-drop-zone").length > 0

      if (isOverAddDropZone) {
        dispatch(addQuestionToInterview(state.isDraggingData.questionId, 0))
      }

      dispatch(stopDraggingQuestion(state.isDraggingData.questionId))

      if (state.isDraggingData.questionOrigin === QUESTION_LIST_VARIANTS.LIBRARY) {
        logCustomGAEvent(GA_CUSTOM_EVENTS.DRAGGED_QUESTION_TO_YOUR_INTERVIEW, null)
      } else {
        logCustomGAEvent(GA_CUSTOM_EVENTS.DRAGGED_QUESTION_TO_LIBRARY, null)
      }
    }

    // Bind all of the drag/drop event listeners
    // It's necessary to do it this way instead of via the React API due to
    // the need to access non-passive events from touchscreens so that we
    // can prevent default on those events

    // Move handlers
    elRef.current.addEventListener("mousemove", handleDragging)
    elRef.current.addEventListener("touchmove", handleDragging, { passive: false })

    // End handlers
    elRef.current.addEventListener("mouseup", handleDragEnd)
    elRef.current.addEventListener("touchend", handleDragEnd)

    // Unbind everything
    return () => {
      // Move handlers
      elRef.current?.removeEventListener("mousemove", handleDragging)
      elRef.current?.removeEventListener("touchmove", handleDragging)

      // End handlers
      elRef.current?.removeEventListener("mouseup", handleDragEnd)
      elRef.current?.removeEventListener("touchend", handleDragEnd)
    }
  }, [elRef.current, initialDragXY, state.isDraggingData])

  return (
    <InterviewBuilderAppGridDIV ref={elRef}>
      {Object.values(QUESTION_LIST_VARIANTS).map((variant) => (
        <QuestionList
          key={variant}
          dispatch={dispatch}
          isCollapsed={currentCollapsedList === variant}
          isMobile={isMobile}
          onAddNewQuestion={() => dispatch(addNewQuestion())}
          onClickExport={() => {
            setShowExportModal(true)
            logCustomGAEvent(GA_CUSTOM_EVENTS.CLICK_EXPORT_INTERVIEW, null)
          }}
          onResetState={handleResetState}
          onToggleCollapsed={() => setCurrentCollapsedList((_variant: QUESTION_LIST_VARIANTS) =>
            _variant === variant ?
              (variant === QUESTION_LIST_VARIANTS.INTERVIEW ? QUESTION_LIST_VARIANTS.LIBRARY : QUESTION_LIST_VARIANTS.INTERVIEW) :
              variant
          )}
          state={state}
          variant={variant}
        />
      ))}
      {state.isDraggingData.questionId && state.isDraggingData.cardClientRect && (
        <DragContainer
          draggingCardClientRect={state.isDraggingData.cardClientRect}
          dragXY={dragXY}
          isMobile={isMobile}
          isDraggingQuestionId={state.isDraggingData.questionId}
          questions={state.questions}
        />
      )}
      {isClientSide() && showExportModal && (
        <ExportModal
          onClose={() => setShowExportModal(false)}
          onClickExport={() => dispatch(exportToPdf())}
          selectedQuestions={state.interview.questions.map(qid => state.questions[qid])}
        />
      )}
    </InterviewBuilderAppGridDIV>
  )
}

export default InterviewBuilderApp
